import { useEffect, type FC, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils/cn';

type ModalSize = 'sm' | 'md' | 'lg';

type Props = {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: ModalSize;
};

const Modal: FC<Props> = ({ open, onClose, title, description, children, footer, size = 'md' }) => {
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const sizes: Record<ModalSize, string> = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl'
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onMouseDown={onClose}
    >
      <div
        className={cn('w-full rounded-lg bg-white shadow', sizes[size])}
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {(title || description) ? (
          <div className="flex items-start justify-between gap-4 border-b border-gray-200 p-6">
            <div>
              {title ? <h3 className="text-xl font-bold text-gray-900">{title}</h3> : null}
              {description ? <p className="mt-1 text-sm text-gray-600">{description}</p> : null}
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-2 text-gray-400 hover:text-gray-600"
              aria-label="Close"
              type="button"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        ) : null}

        <div className="p-6">{children}</div>

        {footer ? (
          <div className="flex items-center justify-end gap-3 border-t border-gray-200 p-6">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Modal;
